/**
 * Props that Base UI overlay triggers (Tooltip, Popover, Menu, Dialog, Drawer,
 * PreviewCard) inject onto the element passed as their `trigger`/`render` target.
 * Internal only — never add these to a config component's public props interface.
 */

/**
 * Forwards the hover/focus/pointer handlers + ARIA/`data-*` markers Base UI
 * injects onto an overlay's trigger, so a config component (Button, Checkbox, …)
 * works as a hover- or focus-opened trigger without a `...restProps` spread.
 * Click-to-open overlays (Popover/Menu/Dialog/Drawer) rely on the trigger config
 * declaring its own `onClick`/`onKeyDown`/`onKeyUp` — those are the config's
 * public API, not injected props, so they are intentionally not forwarded here.
 *
 * Only known keys are copied, so nothing unrelated leaks through. Spread the result
 * **last** on the root so the reconciled values below win:
 *
 * - Handlers / `aria-haspopup` / `data-*` — forwarded as-is (the config never writes these).
 * - `aria-expanded` / `aria-controls` — the config exposes these as structured props
 *   (`ariaExpanded?: { root?: boolean }`) while Base UI injects the flat attribute.
 *   Coalesce: the consumer's explicit structured value wins if set, else the overlay's
 *   live injected value.
 * - `id` — same key is used by the structured config prop (`{ root?: string }`) and the
 *   flat string Base UI injects; a `typeof` guard disambiguates (string = host, object = config).
 *
 * @example
 * return <ButtonRoot id={id?.root} ... {...pickOverlayTriggerProps<HTMLButtonElement>(props)} />;
 */
export const pickOverlayTriggerProps = props => {
  var _source$ariaExpanded$, _source$ariaExpanded, _source$ariaControls$, _source$ariaControls, _source$id;
  const source = props;
  return {
    onBlur: source.onBlur,
    onFocus: source.onFocus,
    onMouseDown: source.onMouseDown,
    onMouseLeave: source.onMouseLeave,
    onMouseMove: source.onMouseMove,
    onMouseOver: source.onMouseOver,
    onPointerDown: source.onPointerDown,
    onPointerEnter: source.onPointerEnter,
    'aria-haspopup': source['aria-haspopup'],
    'aria-expanded': (_source$ariaExpanded$ = (_source$ariaExpanded = source.ariaExpanded) === null || _source$ariaExpanded === void 0 ? void 0 : _source$ariaExpanded.root) !== null && _source$ariaExpanded$ !== void 0 ? _source$ariaExpanded$ : source['aria-expanded'],
    'aria-controls': (_source$ariaControls$ = (_source$ariaControls = source.ariaControls) === null || _source$ariaControls === void 0 ? void 0 : _source$ariaControls.root) !== null && _source$ariaControls$ !== void 0 ? _source$ariaControls$ : source['aria-controls'],
    id: typeof source.id === 'string' ? source.id : (_source$id = source.id) === null || _source$id === void 0 ? void 0 : _source$id.root,
    'data-base-ui-tooltip-trigger': source['data-base-ui-tooltip-trigger'],
    'data-base-ui-click-trigger': source['data-base-ui-click-trigger'],
    'data-popup-open': source['data-popup-open'],
    'data-trigger-disabled': source['data-trigger-disabled']
  };
};